/* eslint-disable prettier/prettier */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { JSX } from 'react';
import {
	ArrowLeft,
	Download,
	Lock,
	AlertCircle,
	CheckCircle,
	ChevronDown,
	Linkedin,
	Mail,
	CircleX,
	FileCheckCorner,
	FileCheck
} from 'lucide-react';
import { useCourseDetails } from '../../hooks/useCourse';
import { useFAQArticles } from '../../hooks/useFaq';
import { useUserProfile } from '../../hooks/useUserProfile';
import styles from '../../styles/pages/learner/LearnerProgramDetailsPage.module.css';
import ProgramPlaceholderImage from '../../assets/images/ProgramMainImg.jpg';
import { useHeader } from '../../context/HeaderContext';
import { ReactComponent as ProgramOwner } from '../../assets/icons/ProgramOwner.svg';
import { ReactComponent as ProgramFormat } from '../../assets/icons/ProgramFormat.svg';
import { ReactComponent as ProgramCertification } from '../../assets/icons/ProgramCertification.svg';
import CoahRupinder from '../../assets/images/ProgramRupinder.png';
import CoahDharmesh from '../../assets/images/ProgramDharmesh.png';
import CoahPeter from '../../assets/images/ProgramPeter.png';
import { useCoachesList } from '../../hooks/useCoachesList';
import {
	useResourceActions,
	useResources,
	useUploadResource,
} from '../../hooks/useResource';
import { formatFileSize } from '../../utils/resourceFileSize';
import ConfirmDownloadModal from '../../components/controlVariations/ConfirmDownloadModal';
import { useCourseAssessments } from '../../hooks/useAssessment';
import { useSubmitAssessment } from '../../hooks/useSubmitAssessment';
import AssessmentStatusCell from '../../components/assessment/AssessmentStatusCell';
import { useRef } from 'react';
import { useAssessmentStatus } from '../../hooks/useAssessmentStatus';
import { AssessmentRow } from '../../components/assessment/AssessmentRow';
import { useDeleteAssessment } from '../../hooks/useDeleteAssessment';
import { useCourseAssessmentStatuses } from '../../hooks/useCourseAssessmentStatuses';
import { useDeleteAssessmentSubmission } from '../../hooks/useDeleteAssessmentSubmission';
import DeleteConfirmModal from '../../components/controlVariations/ConfirmDelete';
import SuccessModal from '../../components/controlVariations/UploadSuccess';
import PopupMobileModal from '../../components/common/PopupMobileModal';
import ResponsiveModal from '../../components/controlVariations/ResponsiveModal';
import { AssessmentStatus } from '../../types/assessment';
import ErrorPopupModal from '../../components/controlVariations/ErrorPopupMoadal';
import { getApiErrorMessage } from '../../utils/getApiErrorMessage';
import ResourceManager, {
	ResourceItem,
} from '../../components/pageComponents/dashboard/coach/ResourceManager';
import AssignmentForm from '../../components/pageComponents/dashboard/coach/AssignmentForm';
import { useCreateAssessment } from '../../hooks/useCreateAssessment';
import ResourceModal, {
	ResourceFile,
} from '../../components/pageComponents/dashboard/coach/ResourceModal';
import SuccessContent from '../../components/controlVariations/ModularSuccessModal';
import ConfirmContent from '../../components/controlVariations/ModularConfirmDelete';
import { useCourseModules } from '../../hooks/useCourseModules';
import { useResource } from '../../hooks/useResource';
import Tooltip from '../../components/common/ToolTip';
import {
	CoachingHours,
	CoachingRecord,
	CoachingStatus,
} from '../../components/pageComponents/dashboard/learner/CoachingHours';
import LogCoachingHours, {
	CoachingHoursFormData,
} from '../../components/pageComponents/dashboard/learner/LogCoachingHours';
import ProgramGoals from '../../components/pageComponents/program/content/ProgramGoals';
import { useGoal } from '../../hooks/useGoal';
import { useCoachingHours } from '../../hooks/useCoachingHours';
import { apiService } from '../../services/api.service';
import UserPlaceholder from '../../assets/images/userPlaceHolder.png';
import {
	FILE_TYPE_MAP,
	getFileTypeLabel,
	getFileExtension,
	normalizeExtension,
} from '../../utils/fileTypeMap';
import ProgramContent from '../../components/pageComponents/program/ProgramContent';
import { formatActivityDateTime, formatModuleDate } from '../../components/pageComponents/program/ProgramContent';

const ProgramDetailsPage: React.FC = () => {
	const { setHeaderContent } = useHeader();
	// Set header content on mount
	useEffect(() => {
		console.log('🔥 setting header');
		setHeaderContent(
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					width: '100%',
				}}
			>
				<h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
					Programs Details
				</h2>
			</div>
		);
		return () => {
			setHeaderContent(null); // cleanup on unmount
		};
	}, []);

	const user = JSON.parse(localStorage.getItem('user') || '{}');

	const { id } = useParams<{ id: string }>();
	const [isMobile, setIsMobile] = useState(false);
	// State and handlers for Confirm Download Modal
	const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
	const [downloadId, setDownloadId] = useState<string | null>(null);
	const [selectedFiles, setSelectedFiles] = useState<Record<string, File[]>>(
		{}
	);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [showAssignmentModal, setShowAssignmentModal] = useState(false);
	const [showCoachingHoursModal, setShowCoachingHoursModal] = useState(false);
	const [assignmentCreateErrors, setAssignmentCreateErrors] = useState<{
		[id: string]: string;
	}>({});
	const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);
	const [stagedFiles, setStagedFiles] = useState<ResourceFile[]>([]);
	const [viewDate, setViewDate] = useState(new Date());
	const [showDeleteAssignmentConfirm, setShowDeleteAssignmentConfirm] =
		useState(false);
	const [pendingDeleteAssignmentId, setPendingDeleteAssignmentId] = useState<
		string | null
	>(null);
	const [coverPreview, setCoverPreview] = useState<string | undefined>(undefined);
	const [openCalendarId, setOpenCalendarId] = useState<string | null>(null);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [pendingDeleteSubmissionId, setPendingDeleteSubmissionId] = useState<
		string | null
	>(null);

	const [uploadProgressLog, setUploadProgressLog] = useState(0);
	const [isUploading, setIsUploading] = useState(false);

	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [pendingDeleteAssessmentId, setPendingDeleteAssessmentId] = useState<
		string | null
	>(null);
	const [assignmentSuccessModal, setAssignmentSuccessModal] = useState({
		open: false,
		title: '',
		subtitle: '',
	});
	const [coachingHoursSuccessModal, setCoachingHoursSuccessModal] = useState({
		open: false,
		title: '',
		subtitle: '',
	});

	const goalFilters = useMemo(
		() => ({
			programid: id,
			userid: user.id,
		}),
		[id]
	);

	const {
		goals,
		loading: goalsLoading,
		error: goalsError,
	} = useGoal(goalFilters);

	const programGoals = goals.map(goal => ({
		id: goal.id,
		title: goal.title,
		targetvalue: goal.targetvalue,
		unit: goal.unit,
		description: goal.description || '',
		goaltype: goal.goaltype || '',
	}));

	const { loading, uploadFile, createLog } = useCoachingHours();

	const {
		logsData,
		fetchLogs,
		loading: coachingLogsLoading,
		error: coachingLogsError,
	} = useCoachingHours();

	useEffect(() => {
		if (!user.id || !id) return;

		fetchLogs({
			learnerid: user.id,
			programid: id,
			page: 1,
			limit: 50,
		});
	}, [user.id, id, fetchLogs]);

	const coachingRecords: CoachingRecord[] = useMemo(() => {
		if (!logsData?.logs) {
			return [];
		}

		return logsData.logs.map(log => ({
			id: log.id,
			name: log.learnerfullname,
			hours: `${(log.durationminutes / 60).toFixed(1)} hrs`,
			date: new Date(log.sessiondate).toLocaleDateString(),
			recordingName: log.file?.filename || 'No File',
			status: (log.status.charAt(0).toUpperCase() +
				log.status.slice(1)) as CoachingStatus,
		}));
	}, [logsData]);

	const [uploadingResources, setUploadingResources] = useState<
		{
			id: string;
			name: string;
			progress: number;
			size: number;
			status: 'uploading' | 'completed' | 'failed';
		}[]
	>([]);
	const { updateResource, loading: updatingResource } = useResource();
	//resource edit states
	const [editingResourceId, setEditingResourceId] = useState<string | null>(
		null
	);
	const [editedResourceValues, setEditedResourceValues] = useState<{
		[key: string]: {
			visibility: boolean;
			downloadable: boolean;
		};
	}>({});
	const [activeResourceLessonFolder, setActiveResourceLessonFolder] =
		useState<{ id: string; name: string } | null>(null);
	const [activeAssignmentLessonFolder, setActiveAssignmentLessonFolder] =
		useState<{ id: string; name: string } | null>(null);
	const [editingRowId, setEditingRowId] = useState<string | null>(null);
	const [editedValues, setEditedValues] = useState<{
		[key: string]: {
			duedate: string;
			visibility: boolean;
		};
	}>({});

	const { remove } = useResourceActions();
	const { uploadResource } = useUploadResource();
	const [editingAssessment, setEditingAssessment] = useState<any | null>(
		null
	);
	const [resourceSuccessModal, setResourceSuccessModal] = useState<{
		open: boolean;
		title: string;
		subtitle: string;
	}>({
		open: false,
		title: '',
		subtitle: '',
	});

	const [showDeleteResourceConfirm, setShowDeleteResourceConfirm] =
		useState(false);
	const [pendingDeleteResourceId, setPendingDeleteResourceId] = useState<
		string | null
	>(null);
	const handleAddFileClick = () => {
		fileInputRef.current?.click();
	};

	useEffect(() => {
		const checkMobile = () => setIsMobile(window.innerWidth <= 768);
		checkMobile();
		window.addEventListener('resize', checkMobile);
		return () => window.removeEventListener('resize', checkMobile);
	}, []);

	// Helper logic to build the calendar grid
	const generateDays = (
		viewDate: { getFullYear: () => any; getMonth: () => any },
		currentDueDate: string | number | Date
	) => {
		const year = viewDate.getFullYear();
		const month = viewDate.getMonth();
		const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0 (Sun) to 6 (Sat)
		const daysInMonth = new Date(year, month + 1, 0).getDate();
		const daysInPrevMonth = new Date(year, month, 0).getDate();

		const grid = [];
		// 1. Padding from previous month
		for (let i = firstDayOfMonth - 1; i >= 0; i--) {
			grid.push({ num: daysInPrevMonth - i, outside: true });
		}
		// 2. Current month days
		for (let i = 1; i <= daysInMonth; i++) {
			const isSelected =
				currentDueDate &&
				new Date(currentDueDate).getDate() === i &&
				new Date(currentDueDate).getMonth() === month &&
				new Date(currentDueDate).getFullYear() === year;
			grid.push({
				num: i,
				selected: isSelected,
				event: [5, 6].includes(i) && month === 2,
			}); // Mock events for March
		}
		// 3. Padding for next month to complete the 6-row grid
		while (grid.length < 42) {
			grid.push({
				num: grid.length - (daysInMonth + firstDayOfMonth) + 1,
				outside: true,
			});
		}
		return grid;
	};

	const hasCoachingHoursGoal = goals?.some(
		goal =>
			goal.goaltype?.toLowerCase() === 'coaching hours'
	);



	const handleFileChangeE = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files || files.length === 0) return;

		const file = files[0];

		if (file.size === 0) {
			showError('File is empty. Please upload a valid file.');
			e.target.value = '';
			return;
		}

		const newFile: ResourceFile = {
			id: crypto.randomUUID(),
			name: file.name,
			file,
			visibility: true,
			downloadable: true,
		};

		setStagedFiles(prev => [...prev, newFile]);
		e.target.value = '';
	};

	const handleToggleVisibility = (id: string) => {
		setStagedFiles(prev =>
			prev.map(f =>
				f.id === id ? { ...f, visibility: !f.visibility } : f
			)
		);
	};

	const handleToggleDownloadable = (id: string) => {
		setStagedFiles(prev =>
			prev.map(f =>
				f.id === id ? { ...f, downloadable: !f.downloadable } : f
			)
		);
	};

	const handleDeleteFile = (id: string) => {
		// remove from staged files and also clear any upload-row bleeding into the background
		setStagedFiles(prev => prev.filter(f => f.id !== id));
		setUploadingResources(prev => prev.filter(r => r.id !== id));
	};

	const handleResourceSubmit = async () => {
		if (!course?.id || stagedFiles.length === 0) {
			showError('Please select at least one file');
			return;
		}

		const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024;

		for (const staged of stagedFiles) {
			if (staged.file.size > MAX_FILE_SIZE_BYTES) {
				showError(`File exceeds the maximum allowed size of 1 GB`);
				return;
			}
		}

		// 🔥 Close modal immediately
		setIsResourceModalOpen(false);

		// 🔥 Create uploading entries (status = uploading)
		const tempUploads = stagedFiles.map(f => ({
			id: f.id,
			name: f.name,
			progress: 0,
			size: f.file.size,
			status: 'uploading' as const,
		}));

		setUploadingResources(prev => [...prev, ...tempUploads]);

		try {
			let allSuccess = true;

			for (const staged of stagedFiles) {
				const result = await uploadResource(
					staged.file,
					{
						title: staged.name,
						resourcetype: 'document',
						relatedcourseid: course.id,
						relatedlessonid: activeResourceLessonFolder?.id,
						visibility: staged.visibility ? 'public' : 'private',
						candownload: staged.downloadable,
					} as any,
					(percent: number) => {
						const safePercent = Math.min(percent, 99); // LIMIT TO 99
						setUploadingResources(prev =>
							prev.map(r =>
								r.id === staged.id
									? { ...r, progress: safePercent }
									: r
							)
						);
					}
				);

				// update state for this file
				setUploadingResources(prev =>
					prev.map(r =>
						r.id === staged.id
							? {
								...r,
								status: result.success
									? 'completed'
									: 'failed',
								progress: result.success ? 100 : r.progress,
							}
							: r
					)
				);

				if (!result.success) {
					allSuccess = false;
					showError(
						`Failed to upload "${staged.name}": ${result.message || 'unknown error'}`
					);
					// do NOT break; continue uploading other files
				} else {
					// remove successful file from staged list
					setStagedFiles(prev =>
						prev.filter(f => f.id !== staged.id)
					);
				}
			}

			if (allSuccess) {
				// Small delay so 100% is visible
				setTimeout(() => {
					setUploadingResources([]);
				}, 500);

				await refreshResources();

				setResourceSuccessModal({
					open: true,
					title: 'Resource Uploaded Successfully',
					subtitle: 'Your file has been uploaded to the course.',
				});

				setTimeout(() => {
					setResourceSuccessModal(prev => ({ ...prev, open: false }));
				}, 3000);
			} else {
				// leave failed rows visible, clear the completed ones after a short delay
				setTimeout(() => {
					setUploadingResources(prev =>
						prev.filter(r => r.status !== 'completed')
					);
				}, 1000);
			}
		} catch (err: any) {
			showError(getApiErrorMessage(err));
			setUploadingResources([]);
		}
	};


	const handleCoachingHoursSubmit = async (data: CoachingHoursFormData) => {
		try {
			let uploadedFileId: string | undefined;

			if (data.file) {

				setIsUploading(true);

				console.log('🔥 Uploading file:', data.file)
				const uploadResponse = await uploadFile(

					data.file,
					(progress) => {
						setUploadProgressLog(progress);
					}
				);

				console.log('🔥 Upload response:', uploadResponse);

				setIsUploading(false);

				if (!uploadResponse) {
					throw new Error(error || 'File upload failed');
				}

				uploadedFileId = uploadResponse.fileid;
			}

			const response = await createLog({
				learnerfullname: data.fullName,
				sessiondate: data.date,
				starttime: data.startTime,
				endtime: data.endTime,
				learnerid: user.id,
				programid: id,
				fileid: uploadedFileId,
			});

			if (response) {
				setShowCoachingHoursModal(false);

				setIsUploading(false);
				setUploadProgressLog(0);
				// success modal
				setCoachingHoursSuccessModal({
					open: true,
					title: 'Coaching Hours Submitted Successfully',
					subtitle:
						'Your coaching hours have been submitted for review.',
				});

				// reload coaching hours table if needed
				await fetchLogs({
					learnerid: user.id,
					programid: id,
					page: 1,
					limit: 50,
				});

				setTimeout(() => {
					setCoachingHoursSuccessModal(prev => ({
						...prev,
						open: false,
					}));
				}, 3000);
			}
			// reload coaching hours table if needed
			// refetchCoachingHours?.();
		} catch (err: any) {
			console.error(err);

			setShowCoachingHoursModal(false);

			setIsUploading(false);
			setUploadProgressLog(0);
			const message = `Error: ${err?.response?.data?.details?.message ||
				err?.response?.data?.message ||
				err?.message ||
				'Failed to submit coaching hours'
				}`;

			requestAnimationFrame(() => {
				setErrorMessage(message);
				setShowErrorModal(true);
			});
		}
	};

	const { create, loading: createLoading } = useCreateAssessment();

	const { profile, loading: profileLoading } = useUserProfile();

	const isCoach =
		profile?.rolename === 'coach' || profile?.rolename === 'mentor';

	const fileInputRef = useRef<HTMLInputElement>(null);
	const showError = (message: string) => {
		setErrorMessage(message);
		setShowErrorModal(true);
	};

	const handleUploadClick = () => {
		// Trigger the hidden input click
		fileInputRef.current?.click();
	};

	const handleFileChange = (
		assessmentId: string,
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		if (!event.target.files) return;

		setSelectedFiles(prev => ({
			...prev,
			[assessmentId]: Array.from(event.target.files!),
		}));
	};

	const {
		assessments,
		loading: assessmentsLoading,
		error: assessmentsError,
		updateAssessment,
		deleteAssessment,
		refetch: refetchAssessments,
	} = useCourseAssessments(id);

	const {
		submit,
		submitting,
		progress,
		error: submitError,
		success,
	} = useSubmitAssessment();

	const resetUploadProgress = (assessmentId: string) => {
		setUploadProgress(prev => {
			const copy = { ...prev };
			delete copy[assessmentId];
			return copy;
		});
	};

	const handleDownloadClick = (id: string, candownload: boolean) => {
		if (!candownload) {
			return;
		}
		setDownloadId(id);
		setShowDownloadConfirm(true);
	};

	const confirmDownload = () => {
		if (!downloadId) return;

		download(downloadId); // ✅ download happens ONLY here

		setShowDownloadConfirm(false);
		setDownloadId(null);
	};

	const cancelDownload = () => {
		setShowDownloadConfirm(false);
		setDownloadId(null);
	};

	const confirmDeleteAssignment = async () => {
		if (!pendingDeleteAssignmentId) return;

		const backendId = assessments.find(
			a => a.id.toString() === pendingDeleteAssignmentId
		)?.id;

		if (!backendId) return;

		try {
			const success = await deleteAssessment(backendId);

			if (success) {
				await refetchAssessments();
				setShowDeleteAssignmentConfirm(false);
				setPendingDeleteAssignmentId(null);

				// 🔥 SHOW SUCCESS MODAL
				setAssignmentSuccessModal({
					open: true,
					title: 'Assignment Deleted Successfully',
					subtitle:
						'The assignment has been removed from the program.',
				});

				setTimeout(() => {
					setAssignmentSuccessModal(prev => ({
						...prev,
						open: false,
					}));
				}, 3000);
			}
			setShowDeleteAssignmentConfirm(false);
		} catch (err) {
			showError(getApiErrorMessage(err));

			console.error('Delete assignment failed', err);
		}
	};
	const handleAutoUpload = async (
		event: React.ChangeEvent<HTMLInputElement>,
		assessment: any
	) => {
		if (!event.target.files?.length) return;

		const files = event.target.files;

		if (!files || files.length === 0) {
			showError(
				'There is no content in this file. Please select appropriate file.'
			);
			return;
		}

		// const selectedFiles = Array.from(files);
		const selectedFiles = Array.from(event.target.files);

		try {
			// 🔒 CLIENT-SIDE FILE TYPE CHECK
			const allowedTypes: string[] = Array.from(
				new Set(
					(assessment.allowedfiletypes || [])
						.map((t: string) => normalizeExtension(t))
						.filter(Boolean)
				)
			);
			const allowedTypesText = allowedTypes
				.map(ext => `.${ext}`)
				.join(', ');

			if (allowedTypes.length === 0) {
				showError(
					'No file types are allowed for this assignment yet. Please contact your coach.'
				);
				event.target.value = '';
				return;
			}

			for (const file of selectedFiles) {
				if (file.size === 0) {
					showError(
						'One of the selected files is empty. Please upload valid content.'
					);
					event.target.value = '';
					return;
				}
				const ext = getFileExtension(file.name);

				if (!ext) {
					showError(
						`This file has no extension and is not allowed. Please upload a file with one of the below extensions: ${allowedTypesText}`
					);
					event.target.value = '';
					return;
				}

				if (!allowedTypes.includes(ext)) {
					showError(
						`.${ext} file is not allowed. Please upload a file with one of the below extensions: ${allowedTypesText}`
					);
					event.target.value = '';
					return;
				}
			}

			// 🔒 CLIENT-SIDE STATIC FILE SIZE CHECK (1 GB MAX)
			const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024; // 1 GB

			for (const file of selectedFiles) {
				if (file.size > MAX_FILE_SIZE_BYTES) {
					showError(
						`File size exceeded. Maximum allowed size is 1 GB`
					);
					event.target.value = '';
					return; // 🚫 STOP upload
				}
			}

			const response = await submit(
				assessment.id,
				{
					files: selectedFiles,
					answers: {}, // future-proof (quiz/text answers)
				},
				percent => {
					setUploadProgress(prev => ({
						...prev,
						[assessment.id]: Math.min(percent, 99),
					}));
				}
			);

			if (!response || response.success === false) {
				throw response;
			}

			// ✅ INSTANT UI UPDATE (NO REFRESH)

			updateAssessmentStatusLocally(assessment.id, {
				has_submitted: true,
				file_name: selectedFiles[0]?.name ?? null,
				submitted_at: new Date(),
			});

			// ✅ SHOW SUCCESS MODAL
			setShowSuccessModal(true);
			setUploadProgress(prev => ({
				...prev,
				[assessment.id]: 100,
			}));
			resetUploadProgress(assessment.id);
			await refetchAssessmentStatuses();
			// auto-close modal
			setTimeout(() => {
				setShowSuccessModal(false);
			}, 3000);
			// reset progress after success

			// Clear input so same file can be re-selected if needed
			event.target.value = '';
		} catch (err: any) {
			showError(getApiErrorMessage(err));

			resetUploadProgress(assessment.id);

			event.target.value = '';
		}
	};

	const {
		statuses: assessmentStatuses,
		loading: statusLoading,
		setStatuses: setAssessmentStatuses,
		refetch: refetchAssessmentStatuses,
	} = useCourseAssessmentStatuses(id);

	const updateAssessmentStatusLocally = (
		assessmentId: string,
		data: Partial<AssessmentStatus>
	) => {
		console.log(
			'[OPTIMISTIC UPDATE]',
			'assessmentId:',
			assessmentId,
			'data:',
			data
		);
		setAssessmentStatuses(prev => {
			console.log('[BEFORE STATE]', prev[assessmentId]);

			const next = {
				...prev,
				[assessmentId]: {
					...prev[assessmentId],
					...data,
				},
			};

			console.log('[AFTER STATE]', next[assessmentId]);

			return next;
		});
	};

	const {
		modules,
		fetchCourseModules,
		loading: modulesLoading,
	} = useCourseModules();

	useEffect(() => {
		if (id) {
			fetchCourseModules(id);
		}
	}, [id, fetchCourseModules]);

	const { resources, refetch: refreshResources } = useResources(
		id ? 'course' : 'all',
		id || ''
	);

	const lessonFolders = React.useMemo(() => {
		return modules.flatMap(module =>
			(module.lessons || []).map(lesson => ({
				id: lesson.id,
				name: lesson.title,
				isFolder: true,
				isActive: true, // ✅ REQUIRED
				visibility: true,
				downloadable: false,
			}))
		);
	}, [modules]);

	const rootFiles = resources
		.filter(res => !res.relatedlessonid)
		.map(res => ({
			id: res.id,
			name: res.title,
			size: formatFileSize(res.filesize),
			isFolder: false,
			raw: res,
			visibility: res.visibility === 'public',
			downloadable: res.candownload,
		}));

	const lessonFiles = activeResourceLessonFolder
		? resources
			.filter(
				res => res.relatedlessonid === activeResourceLessonFolder.id
			)
			.map(res => ({
				id: res.id,
				name: res.title,
				size: formatFileSize(res.filesize),
				isFolder: false,
				raw: res,
				visibility: res.visibility === 'public',
				downloadable: res.candownload,
			}))
		: [];
	const rootAssignments = assessments
		.filter(a => !a.lessonid)
		.map(a => ({
			id: a.id.toString(),
			backendId: a.id,
			name: a.title,
			type: a.allowedfiletypes?.[0] || '',
			dueDate: a.duedate
				? new Date(a.duedate).toISOString().split('T')[0]
				: '',

			isFolder: false,
			isActive: a.allowfilesubmission,
		}));

	const lessonAssignments = activeAssignmentLessonFolder
		? assessments
			.filter(a => a.lessonid === activeAssignmentLessonFolder.id)
			.map(a => ({
				id: a.id.toString(),
				backendId: a.id,
				name: a.title,
				type: a.allowedfiletypes?.[0] || '',
				dueDate: a.duedate
					? new Date(a.duedate).toISOString().split('T')[0]
					: '',

				isFolder: false,
				isActive: a.allowfilesubmission,
			}))
		: [];

	const displayedAssignmentData = activeAssignmentLessonFolder
		? lessonAssignments
		: [...lessonFolders, ...rootAssignments];

	const uploadingRows: ResourceItem[] = uploadingResources.map(
		r =>
			({
				id: r.id,
				name: r.name,
				isFolder: false,
				status: r.status, // already correct union type
				// 👇 Only mark as uploading on MOBILE
				isUploading: isMobile ? r.status === 'uploading' : false,
				progress: r.progress,
				size: formatFileSize(r.size),
			}) as ResourceItem
	);

	const displayedResourceData = activeResourceLessonFolder
		? lessonFiles
		: [...lessonFolders, ...uploadingRows, ...rootFiles];

	const { deleteSubmission, deleting: deletingSubmission } =
		useDeleteAssessmentSubmission();

const confirmDeleteSubmission = async () => {
    console.log('[DELETE CONFIRM] clicked');

    if (!pendingDeleteSubmissionId || !pendingDeleteAssessmentId) {
        console.log('[DELETE CONFIRM] missing ids', {
            pendingDeleteSubmissionId,
            pendingDeleteAssessmentId,
        });
        return;
    }

    console.log('[DELETE CONFIRM] ids OK', {
        submissionId: pendingDeleteSubmissionId,
        assessmentId: pendingDeleteAssessmentId,
    });

    try {
        // ✅ Delete from backend FIRST
        await deleteSubmission(pendingDeleteSubmissionId);

        console.log('[DELETE CONFIRM] backend delete success');

        // ✅ Only update local UI AFTER successful API response
        updateAssessmentStatusLocally(pendingDeleteAssessmentId, {
            has_submitted: false,
            submission_id: undefined,
            passed: null,
            file_name: null,
            submitted_at: undefined,
        });

        console.log('[DELETE CONFIRM] local state updated');

        // ✅ Refresh statuses
        await refetchAssessmentStatuses();

        console.log('[DELETE CONFIRM] refetch done');

        setShowDeleteConfirm(false);
        setPendingDeleteSubmissionId(null);
        setPendingDeleteAssessmentId(null);
    } catch (e: any) {
        // ❌ API failed → DO NOT change local assessment state
        console.error('[DELETE CONFIRM] error', e);

        setShowDeleteConfirm(false);
        setPendingDeleteSubmissionId(null);
        setPendingDeleteAssessmentId(null);

        // ✅ Show API error modal
        showError(getApiErrorMessage(e));
    }
};

	const cancelDeleteSubmission = () => {
		setShowDeleteConfirm(false);
		setPendingDeleteSubmissionId(null);
	};

	const handleDeleteSubmission = (
		submissionId: string,
		assessmentId: string
	) => {
		setPendingDeleteSubmissionId(submissionId);
		setPendingDeleteAssessmentId(assessmentId);
		setShowDeleteConfirm(true);
	};

	const handleAddFolder = (type: string) => {
		console.log('Add folder for', type);
	};

	const handleFileUpload = (type: string) => {
		console.log('File upload for', type);
	};

	const confirmDeleteResource = async () => {
		if (!pendingDeleteResourceId) return;

		try {
			const success = await remove(pendingDeleteResourceId);

			if (success) {
				setShowDeleteResourceConfirm(false);
				setPendingDeleteResourceId(null);

				// Optional: show success modal
				setResourceSuccessModal({
					open: true,
					title: 'Resource Deleted Successfully',
					subtitle: 'The resource has been removed from the course.',
				});

				setTimeout(() => {
					setResourceSuccessModal(prev => ({ ...prev, open: false }));
				}, 3000);
			}
			await refreshResources();
		} catch (err) {
			console.error('Delete failed', err);
		}
	};

	const cancelDeleteResource = () => {
		setShowDeleteResourceConfirm(false);
		setPendingDeleteResourceId(null);
	};

	const dueAssignmentsCount = React.useMemo(() => {
		if (!assessments?.length) {
			console.log('📊 Count Debug: No assessments found yet.');
			return 0;
		}

		const count = assessments.filter(assessment => {
			const status = assessmentStatuses[assessment.id];
			const isSubmitted = status?.has_submitted === true;

			console.log(`🔍 Checking Assessment [${assessment.title}]:`, {
				id: assessment.id,
				statusFound: !!status,
				isSubmitted: isSubmitted,
			});

			return !isSubmitted; // We want to count ones NOT submitted
		}).length;

		console.log('✅ Final Due Count:', count);
		return count;
	}, [assessments, assessmentStatuses]);

	const navigate = useNavigate();
	const FAQs_PER_PAGE = 5;
	const [faqPage, setFaqPage] = useState(1);

	const { course, isLoading, error } = useCourseDetails(id || '');
	const { coaches, loading: coachesLoading } = useCoachesList('coach');
	const { download, isProcessing } = useResourceActions();
	const coachImages = [CoahRupinder, CoahDharmesh, CoahPeter];
	// Add this state to track which Assessment is being modified
	const {
		articles: faqs,
		isLoading: isFaqLoading,
		error: faqError,
	} = useFAQArticles({
		category: course?.category, // Filters FAQs relevant to this program's category
		// ispublished: true,
	});
	// State for Accordion
	const [activeSection, setActiveSection] = useState<number | null>(0);
	const [activeFaq, setActiveFaq] = useState<number | null>(null);
	const [uploadProgress, setUploadProgress] = useState<
		Record<string, number>
	>({});

	const totalFaqPages = Math.ceil(faqs.length / FAQs_PER_PAGE);

	const paginatedFaqs = faqs.slice(
		(faqPage - 1) * FAQs_PER_PAGE,
		faqPage * FAQs_PER_PAGE
	);

	useEffect(() => {
		if (assessmentsError) {
			showError(assessmentsError);
		}
	}, [assessmentsError]);

	useEffect(() => {
		if (submitError) {
			showError(submitError);
		}
	}, [submitError]);


	// Compute dynamic counts based on submission status and due dates
	const { dueCount, overdueCount, submittedCount } = useMemo(() => {
		let due = 0;
		let overdue = 0;
		let submitted = 0;
		const now = new Date();

		assessments.forEach(assessment => {
			const status = assessmentStatuses[assessment.id];
			const isSubmitted = Boolean(status?.has_submitted);

			if (isSubmitted) {
				submitted++;
			}
			else if (
				assessment.duedate &&
				new Date(assessment.duedate).setHours(0, 0, 0, 0) <
				new Date().setHours(0, 0, 0, 0)
			) {
				overdue++;
			}
			else {
				due++;
			}
		});

		return { dueCount: due, overdueCount: overdue, submittedCount: submitted };
	}, [assessments, assessmentStatuses]);

	// Compute review status counts based only on `passed`
	const { pendingCount, approvedCount, rejectedCount } = useMemo(() => {
		let pending = 0;
		let approved = 0;
		let rejected = 0;

		assessments.forEach(assessment => {
			const status = assessmentStatuses[assessment.id];
			const passed = status?.passed;

			if (passed === true) {
				approved++;
			} else if (passed === false) {
				rejected++;
			} else {
				pending++;
			}
		});

		return {
			pendingCount: pending,
			approvedCount: approved,
			rejectedCount: rejected,
		};
	}, [assessments, assessmentStatuses]);


	useEffect(() => {
		const fetchCover = async () => {
			try {
				const res = await apiService.get<{ picture: string }>(`course/${course?.id}/cover`);
				setCoverPreview(res?.data?.picture);
			} catch (err) {
				console.error("Cover fetch failed");
			}
		};

		if (course?.id) fetchCover();
	}, [course?.id]);

	if (isLoading) return <div className={styles.loading}>Loading...</div>;
	if (error || !course)
		return <div className={styles.error}>Error: {error}</div>;

	const handleLogCoachingHours = () => {
		setShowCoachingHoursModal(true);
	};

	const formattedModules = (modules || []).map((module: any, index: number) => {
		const lessons = (module.lessons || []).map(
			(lesson: {
				id: any;
				title: any;
				scheduledstarttime: any;
				scheduledendtime: any;
				activitytype?: {
					id: string;
					title: string;
					color?: string;
					icon?: string | null;
				};
				metadata: any;
				duration: any;
				islive?: boolean;
				description?: string;
			}, i: number) => ({
				id: lesson.id,
				sequence: i + 1,
				title: lesson.title,
				date: formatModuleDate(lesson.scheduledstarttime || ''),
				dateTimeStr: formatActivityDateTime(
					lesson.scheduledstarttime,
					lesson.scheduledendtime
				),
				type: lesson.activitytype?.title || 'Session',
				typeColor: lesson.activitytype?.color || '#F97316',
				participants: lesson.metadata ? ['User1', 'User2'] : [],
				isCompleted: lesson.islive === false,
				duration: lesson.duration || 0,
				description: lesson.description || '',
			})
		);

		console.log("⚙️ Mapping module 👉", module);

		return {
			id: module.id,
			sequence: index + 1,
			title: module.title || '',


			startdate: module.startdate || '',
			enddate: module.enddate || '',

			description: module.description || '',
			isCompleted: false,
			lessons,
			totalActivities: lessons.length,
		};
	});

	return (
		<div className={styles.container}>
			<input
				type="file"
				ref={fileInputRef}
				style={{ display: 'none' }}
				onChange={handleFileChangeE}
			/>

			{/* --- Top Navigation --- */}
			<div className={styles.navHeader}>
				<button className={styles.backBtn} onClick={() => navigate(-1)}>
					<ArrowLeft size={20} /> Back
				</button>
			</div>

			{/* --- Main Info Section --- */}
			<div className={styles.mainGrid}>
				<div className={styles.leftCol}>
					<nav className={styles.breadcrumbs}>
						{course.sol_title}{' '}
						<span>
							{' '}
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="8"
								height="14"
								viewBox="0 0 8 14"
								fill="none"
							>
								<path
									d="M0.75 12.75L6.75 6.75L0.75 0.75"
									stroke="#949494"
									stroke-width="1.5"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>{' '}
						</span>{' '}
						{course.title}
					</nav>
					<h1 className={styles.mainTitle}>{course.title}</h1>

					<section className={styles.section2}>
						<h3 className={styles.sectionTitle}>Description</h3>
						<p className={styles.descriptionText}>
							{course.description}
						</p>
					</section>

					{/* --- Program Includes --- */}
					{course.programincludes &&
						course.programincludes.length > 0 ? (
						<section className={styles.section}>
							<div className={styles.sectionHeaderLine}>
								<h3 className={styles.sectionTitle}>
									Program Includes:
								</h3>
								<div className={styles.line}></div>
							</div>

							<ul className={styles.includesList}>
								{course.programincludes.map(
									(item: string, index: number) => (
										<li key={index}>{item}</li>
									)
								)}
							</ul>
						</section>
					) : (
						<section className={styles.section}>
							<p className={styles.emptyText}>
								Program details will be updated soon.
							</p>
						</section>
					)}
				</div>

				<div className={styles.rightCol}>
					<div className={styles.stickyCard}>
						<img
							src={coverPreview || ProgramPlaceholderImage}
							alt="Course"
							className={styles.featuredImg}
						/>
						<div className={styles.metaTable}>
							{/* Program Owner Section */}
							<div className={styles.metaInfoRow}>
								<div className={styles.metaIcon}>
									<ProgramOwner />
								</div>
								<div className={styles.metaText}>
									<span className={styles.metaLabel}>
										Program Owner & Lead Faculty:
									</span>
									<strong className={styles.metaValue}>
										{course.programownername ||
											'Not Specified'}
									</strong>
								</div>
							</div>

							{/* Program Format Section */}
							<div className={styles.metaInfoRow}>
								<div className={styles.metaIcon}>
									<ProgramFormat />
								</div>
								<div className={styles.metaText}>
									<span className={styles.metaLabel}>
										Program Format:
									</span>
									<strong className={styles.metaValue}>
										{course.programformat || 'Not specified'}
									</strong>
								</div>
							</div>

							{/* Certification Section */}
							<div className={styles.metaInfoRow}>
								<div className={styles.metaIcon}>
									<ProgramCertification />
								</div>
								<div className={styles.metaText}>
									<span className={styles.metaLabel}>
										Certification:
									</span>
									<strong className={styles.metaValue}>
										{course.certification ||
											'Not specified'}
									</strong>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* --- What You'll Learn --- */}

			{course.learningtakeaways &&
				course.learningtakeaways.length > 0 && (
					<div className={styles.fullWidthSectionLearn}>
						<div className={styles.sectionHeaderLine}>
							<h3 className={styles.sectionTitle}>
								What you'll learn
							</h3>
							<div className={styles.line}></div>
						</div>

						<div className={styles.learningGrid}>
							{course.learningtakeaways.map(
								(point: string, i: number) => (
									<div
										key={i}
										className={styles.learningItem}
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="27"
											height="27"
											viewBox="0 0 27 27"
											fill="none"
										>
											<path
												d="M25.4983 10.7542C26.0692 13.5559 25.6623 16.4685 24.3456 19.0065C23.0289 21.5444 20.8819 23.5543 18.2627 24.7009C15.6435 25.8475 12.7103 26.0615 9.95244 25.3072C7.19453 24.5529 4.77856 22.876 3.10742 20.556C1.43627 18.236 0.610975 15.4133 0.769153 12.5585C0.92733 9.70367 2.05942 6.98937 3.97663 4.86822C5.89385 2.74707 8.48029 1.3473 11.3046 0.902326C14.129 0.457355 17.0205 0.994084 19.4971 2.42301M9.49706 12.0042L13.2471 15.7542L25.747 3.25426"
												stroke="#147B58"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>

										<p>{point}</p>
									</div>
								)
							)}
						</div>
					</div>
				)}

			{programGoals?.length > 0 ? (
				<div className={styles.fullWidthSectionLearn}>
					<ProgramGoals title="Program Goals" goals={programGoals} />
				</div>
			) : (
				// <div className={goalStyles.emptyContainer}>
				<h2>No Program Goals Available</h2>
				// </div>
			)}

			<div className={styles.fullWidthSection}>

				<ProgramContent
					modules={formattedModules}
					showMarkCompleteButton={false}
				/>
			</div>

			<div className={styles.fullWidthSectionResourcesUpload}>
				{/* --- Resources Section --- */}
				{/* --- RESOURCES SECTION --- */}
				<div className={styles.fullWidthSectionRU}>
					<div className={styles.resourceTableWrapper}>
						<h3 className={styles.sectionTitle}>
							Program Resources
						</h3>

						{/* Handle Empty State */}
						{resources.length === 0 && !isLoading ? (
							<p className={styles.noData}>
								No resources available for this Program.
							</p>
						) : (
							<table className={styles.resourceTable}>
								<thead>
									<tr className={styles.resourceRow}>
										<th>Name</th>
										<th>File Type</th>
										<th>Size</th>
										<th style={{ textAlign: 'center' }}>
											Download
										</th>
									</tr>
								</thead>
								<tbody>
									{resources.map(res => (
										<tr
											key={res.id}
											className={styles.resourceRow}
										>
											<td className={styles.fileName}>
												{res.title}
												{/* If it's a link, maybe show a small badge */}
												{res.resourcetype ===
													'link' && (
														<span
															className={
																styles.linkBadge
															}
														>
															Link
														</span>
													)}
											</td>
											<td>
												{/* Show the mimetype (e.g., application/pdf) or a cleaner version */}
												{res.filename
													?.split('.')[1]
													?.toUpperCase() || 'URL'}
											</td>
											<td>
												{formatFileSize(
													res?.filesize
												) || '--'}
											</td>
											<td className={styles.actionsCell}>
												<button
													className={`${styles.actionIcon} ${!res.candownload
														? styles.disabledAction
														: ''
														}`}
													onClick={() =>
														handleDownloadClick(
															res.id,
															res.candownload
														)
													}
													disabled={
														!res.candownload ||
														isProcessing
													}
													title={
														res.candownload
															? 'Download Resource'
															: 'You do not have permission to download this resource'
													}
												>
													{res.candownload ? (
														<Download size={18} />
													) : (
														<Lock size={16} />
													)}
												</button>

												<ResponsiveModal
													open={showDownloadConfirm}
													onClose={cancelDownload}
												>
													<ConfirmDownloadModal
														onConfirm={
															confirmDownload
														}
														onCancel={
															cancelDownload
														}
													/>
												</ResponsiveModal>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				</div>
			</div>

			{/* --- Upload Assignment Section --- */}
			{/* --- ASSESSMENTS SECTION --- */}
			{/* --- ASSESSMENTS SECTION --- */}
			<div className={styles.fullWidthSectionRU}>
				<div className={styles.uploadHeader}>
					<h3 className={styles.sectionTitle}>Upload Assignment</h3>

					{!statusLoading && assessments.length > 0 && (
						<div className={styles.statusIndicatorsGroup}>
							{dueCount > 0 && (
								<div className={styles.indicatorDue}>
									<span>{dueCount} Due</span>
									<AlertCircle size={16} />
								</div>
							)}
							{overdueCount > 0 && (
								<div className={styles.indicatorOverdue}>
									<span>{overdueCount} Overdue</span>
									<AlertCircle size={16} />
								</div>
							)}
							{submittedCount > 0 && (
								<div className={styles.indicatorSubmitted}>
									<span>{submittedCount} Submitted</span>
									<CheckCircle size={16} />
								</div>
							)}
							{/* {pendingCount > 0 && (
								<div className={styles.indicatorPending}>
									<span>{pendingCount} Pending</span>
									<AlertCircle size={16} />
								</div>
							)}

							{approvedCount > 0 && (
								<div className={styles.indicatorApproved}>
									<span>{approvedCount} Approved</span>
									<FileCheck size={16} />
								</div>
							)}

							{rejectedCount > 0 && (
								<div className={styles.indicatorRejected}>
									<span>{rejectedCount} Rejected</span>
									<CircleX size={18} />
								</div>
							)} */}
						</div>
					)}
				</div>

				{/* Loading */}
				{assessmentsLoading && (
					<p className={styles.noData}>Loading assignments...</p>
				)}

				{/* Empty */}
				{!assessmentsLoading && assessments.length === 0 && (
					<p className={styles.noData}>
						No assignments available for this program.
					</p>
				)}

				{/* Table */}
				{!assessmentsLoading && assessments.length > 0 && (
					<div className={styles.resourceTableWrapper}>
						<table className={styles.resourceTable}>
							<thead>
								<tr className={styles.resourceRow}>
									<th>Assignment Title</th>
									<th className={styles.uploadedFileHeader}>File Name</th>
									<th className={styles.uploadedDateHeader}>Uploaded Date</th>
									<th>File Type</th>
									<th>Due Date</th>
									<th style={{ textAlign: 'center' }}>Status</th>
									<th style={{ textAlign: 'center' }}>State</th>
									<th style={{ textAlign: 'center' }}>Actions</th>
								</tr>
							</thead>

							<tbody>
								{assessments.map(assessment => (
									<AssessmentRow
										key={assessment.id}
										assessment={assessment}
										showFileDetails
										status={assessmentStatuses[assessment.id]}
										passed={assessmentStatuses[assessment.id]?.passed ?? null}
										submitting={submitting}
										uploadProgress={uploadProgress}
										onUpload={handleAutoUpload}
										onDelete={handleDeleteSubmission}
									/>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{/* --- Coaching Hours Section --- */}
			<div className={styles.fullWidthSectionRU}>
				{hasCoachingHoursGoal && (<CoachingHours
					records={coachingRecords}
					loading={coachingLogsLoading}
					onLogHours={handleLogCoachingHours}
				/>)}

			</div>

			{/* --- Meet Your Coaches Section --- */}

			<div className={styles.fullWidthSection}>
				<div className={styles.sectionHeaderLine}>
					<h3 className={styles.sectionTitle}>Meet Your Coaches</h3>
					<div className={styles.line}></div>
				</div>

				<div className={styles.coachesGrid}>
					{!course?.faculty?.length && <p>No coaches assigned</p>}

					{course?.faculty?.map((facultyItem: any, i: number) => {
						const user = facultyItem.user;

						return (
							<div
								key={facultyItem.id}
								className={styles.coachCard}
							>
								<div className={styles.coachHeader}>
									<img
										src={
											user?.profilepicture || UserPlaceholder // fallback
										}
										alt={user?.fullname || 'Coach'}
										className={styles.coachImg}
									/>

									<div className={styles.coachMainInfo}>
										<h4 className={styles.coachName}>
											{user?.fullname || 'Unknown Coach'}
										</h4>

										<p className={styles.coachRole}>
											{facultyItem.facultyrole || 'Coach'}
										</p>

										<div
											className={styles.coachSocials}
											style={{ display: 'none' }}
										>
											<Linkedin size={18} />
											<Mail size={18} />
										</div>
									</div>
								</div>

								<p className={styles.coachBio}>
									{user?.bio || 'No bio available'}
								</p>
							</div>
						);
					})}
				</div>
			</div>

			{/* --- FAQs & Terms Split Section --- */}
			<div className={`${styles.fullWidthSection} ${styles.bottomGrid}`}>
				{/* FAQs Left */}
				<h3 className={styles.sectionTitle}>FAQs</h3>
				<div className={styles.faqContainer}>
					{isFaqLoading && <p>Loading FAQs...</p>}

					{!isFaqLoading && faqs.length === 0 && (
						<p className={styles.descriptionText}>
							No FAQs available for this category yet.
						</p>
					)}

					{paginatedFaqs.map((item, idx) => {
						const globalIndex = (faqPage - 1) * FAQs_PER_PAGE + idx;
						return (
							<div key={item.id} className={styles.faqItem}>
								<div
									className={styles.faqHeader}
									onClick={() =>
										setActiveFaq(
											activeFaq === globalIndex
												? null
												: globalIndex
										)
									}
								>
									{/* Display title/question from DB */}
									<span className={styles.faqQuestion}>
										{item.title}
									</span>
									<ChevronDown
										className={
											activeFaq === globalIndex
												? styles.rotate
												: ''
										}
										size={18}
									/>
								</div>

								{activeFaq === globalIndex && (
									<div className={styles.faqBody}>
										{/* Display content from DB */}
										<p>{item.content}</p>

										{/* Optional: Show if it was helpful (using your DB counts) */}
										<div
											style={{
												marginTop: '10px',
												fontSize: '12px',
												color: '#666',
											}}
										>
											{item.helpfulcount} people found
											this helpful
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>

				{totalFaqPages > 1 && (
					<div className={styles.pagination}>
						<button
							disabled={faqPage === 1}
							onClick={() => {
								setFaqPage(p => p - 1);
								setActiveFaq(null);
							}}
						>
							Previous
						</button>

						{Array.from({ length: totalFaqPages }).map((_, i) => (
							<button
								key={i}
								className={
									faqPage === i + 1 ? styles.activePage : ''
								}
								onClick={() => {
									setFaqPage(i + 1);
									setActiveFaq(null);
								}}
							>
								{i + 1}
							</button>
						))}

						<button
							disabled={faqPage === totalFaqPages}
							onClick={() => {
								setFaqPage(p => p + 1);
								setActiveFaq(null);
							}}
						>
							Next
						</button>
					</div>
				)}
			</div>

			<ResponsiveModal
				open={showDeleteConfirm}
				onClose={cancelDeleteSubmission}
			>
				<DeleteConfirmModal
					onDelete={confirmDeleteSubmission}
					onCancel={cancelDeleteSubmission}
				/>
			</ResponsiveModal>

			<ResponsiveModal
				open={showDeleteResourceConfirm}
				onClose={cancelDeleteResource}
			>
				<ConfirmContent
					title="Delete This Resource?"
					// message="This action cannot be undone. Are you sure you want to delete this resource?"
					confirmText="Delete"
					cancelText="Cancel"
					onConfirm={confirmDeleteResource}
					onCancel={cancelDeleteResource}
				/>
			</ResponsiveModal>

			<ResponsiveModal
				open={resourceSuccessModal.open}
				onClose={() =>
					setResourceSuccessModal(prev => ({ ...prev, open: false }))
				}
			>
				<SuccessContent
					title={resourceSuccessModal.title}
					subtitle={resourceSuccessModal.subtitle}
				/>
			</ResponsiveModal>

			<ResponsiveModal
				open={showSuccessModal}
				onClose={() => setShowSuccessModal(false)}
			>
				<SuccessModal />
			</ResponsiveModal>

			<ResponsiveModal
				open={showErrorModal}
				onClose={() => setShowErrorModal(false)}
			>
				<ErrorPopupModal
					title={errorMessage}
					onCancel={() => setShowErrorModal(false)}
				/>
			</ResponsiveModal>

			<ResponsiveModal
				open={showAssignmentModal}
				onClose={() => setShowAssignmentModal(false)}
			>
				<AssignmentForm
					initialData={
						editingAssessment
							? {
								title: editingAssessment.title,
								dueDate: editingAssessment.duedate,
								type:
									editingAssessment
										.allowedfiletypes?.[0] || '',
								isVisible:
									editingAssessment.allowfilesubmission,
							}
							: undefined
					}
					isEditMode={!!editingAssessment}
					assignmentCreateErrors={assignmentCreateErrors}
					onSubmit={async assignments => {
						// 🔥 IN EDIT MODE: Use only first assignment (single update)
						if (editingAssessment) {
							const assignment = assignments[0];
							try {
								await updateAssessment(editingAssessment.id, {
									title: assignment.title,
									duedate: assignment.dueDate
										? new Date(
											assignment.dueDate
										).toISOString()
										: undefined,
									allowfilesubmission: assignment.isVisible,
									allowedfiletypes: FILE_TYPE_MAP[assignment.type.toLowerCase()] ?? [],
									lessonid: activeAssignmentLessonFolder?.id,
								});

								setAssignmentSuccessModal({
									open: true,
									title: 'Assignment Updated Successfully',
									subtitle:
										'The assignment has been updated.',
								});

								setEditingAssessment(null);
								setShowAssignmentModal(false);
								await refetchAssessments();

								setTimeout(() => {
									setAssignmentSuccessModal(prev => ({
										...prev,
										open: false,
									}));
								}, 3000);
							} catch (err: any) {
								showError(getApiErrorMessage(err));
							}
							return;
						}

						// 🔥 IN CREATE MODE: Batch create multiple assignments with error tracking
						const createdIds = new Set<string>();
						const failedAssignments = new Map<string, string>();

						for (const assignment of assignments) {
							try {
								await create({
									courseid: id,
									lessonid: activeAssignmentLessonFolder?.id,
									title: assignment.title,
									duedate: new Date(
										assignment.dueDate
									).toISOString(),
									allowfilesubmission: assignment.isVisible,
									requirefilesubmission: true,
									allowedfiletypes: FILE_TYPE_MAP[assignment.type.toLowerCase()] ?? [],
								});
								createdIds.add(assignment.id);
							} catch (err: any) {
								const errorMsg = getApiErrorMessage(err);
								failedAssignments.set(assignment.id, errorMsg);
							}
						}

						// 🔥 If no failures, show success and close
						if (failedAssignments.size === 0) {
							setAssignmentCreateErrors({});
							setAssignmentSuccessModal({
								open: true,
								title:
									assignments.length === 1
										? 'Assignment Created Successfully'
										: `${assignments.length} Assignments Created Successfully`,
								subtitle:
									'The assignment(s) have been added to the program.',
							});

							setShowAssignmentModal(false);
							await refetchAssessments();

							setTimeout(() => {
								setAssignmentSuccessModal(prev => ({
									...prev,
									open: false,
								}));
							}, 3000);
							return;
						}

						// 🔥 If some or all failed: track errors and keep modal open for removal/retry
						setAssignmentCreateErrors(
							Object.fromEntries(failedAssignments)
						);

						const successCount = createdIds.size;
						const failureCount = failedAssignments.size;

						if (successCount > 0) {
							showError(
								`${successCount} created, ${failureCount} failed. Remove failed items and try again.`
							);
							await refetchAssessments();
						} else {
							showError(
								`All ${failureCount} assignments failed to create. Fix errors and try again.`
							);
						}
					}}
					onClose={() => setShowAssignmentModal(false)}
				/>
			</ResponsiveModal>

			<ResponsiveModal
				open={showCoachingHoursModal}
				onClose={() => setShowCoachingHoursModal(false)}
			>
				<LogCoachingHours
					onClose={() => setShowCoachingHoursModal(false)}
					uploadProgress={uploadProgressLog}
					isUploading={isUploading}
					learnerId={user.id}
					programId={id}
					onSubmit={handleCoachingHoursSubmit}
				/>
			</ResponsiveModal>

			<ResponsiveModal
				open={showDeleteAssignmentConfirm}
				onClose={() => setShowDeleteAssignmentConfirm(false)}
			>
				<ConfirmContent
					title="Are you sure you want to delete this assignment?"
					confirmText="Delete"
					cancelText="Cancel"
					onConfirm={confirmDeleteAssignment}
					onCancel={() => setShowDeleteAssignmentConfirm(false)}
				/>
			</ResponsiveModal>

			<ResponsiveModal
				open={assignmentSuccessModal.open}
				onClose={() =>
					setAssignmentSuccessModal(prev => ({
						...prev,
						open: false,
					}))
				}
			>
				<SuccessContent
					title={assignmentSuccessModal.title}
					subtitle={assignmentSuccessModal.subtitle}
				/>
			</ResponsiveModal>

			<ResponsiveModal
				open={coachingHoursSuccessModal.open}
				onClose={() =>
					setCoachingHoursSuccessModal(prev => ({
						...prev,
						open: false,
					}))
				}
			>
				<SuccessContent
					title={coachingHoursSuccessModal.title}
					subtitle={coachingHoursSuccessModal.subtitle}
				/>
			</ResponsiveModal>

			{isResourceModalOpen && (
				<ResourceModal
					files={stagedFiles}
					onClose={() => setIsResourceModalOpen(false)}
					onAddFile={handleAddFileClick}
					onDeleteFile={handleDeleteFile}
					onToggleVisibility={handleToggleVisibility}
					onToggleDownloadable={handleToggleDownloadable}
					onSubmit={handleResourceSubmit}
				/>
			)}
		</div>
	);
};

export default ProgramDetailsPage;
